import { useEffect, useState } from "react";
import { BREAKPOINTS } from "../constants/breakpoints";

/**
 * Returns the current viewport breakpoint tier plus boolean helpers.
 *
 * Updates on window resize (debounced via requestAnimationFrame so
 * resize storms do not thrash renders).
 *
 * Prefer this over the older useIsMobile.ts, which only returns a
 * single boolean. useBreakpoint gives you tablet/desktop/wide too,
 * which is needed for 3-breakpoint layouts (stat row 1 col -> 2 col
 * -> 4 col, for example).
 *
 * Usage:
 *   const { isMobile, isTablet, isDesktop, tier } = useBreakpoint();
 *   if (isMobile) return <MobileLayout />;
 *   return <DesktopLayout />;
 *
 * SSR safety: returns width 0 (and tier "mobile") on the server.
 * The useEffect syncs to the real width on mount. For a pure client
 * app like Novala (Vite CSR), this never matters, but it keeps the
 * hook safe to use anywhere.
 */

export type BreakpointTier = "mobile" | "tablet" | "desktop" | "wide";

export interface BreakpointState {
  /** Current viewport width in pixels. 0 during SSR. */
  width: number;
  /** True when width < BREAKPOINTS.tablet (640). */
  isMobile: boolean;
  /** True when BREAKPOINTS.tablet <= width < BREAKPOINTS.desktop. */
  isTablet: boolean;
  /** True when BREAKPOINTS.desktop <= width < BREAKPOINTS.wide. */
  isDesktop: boolean;
  /** True when width >= BREAKPOINTS.wide. */
  isWide: boolean;
  /** The named tier for switch statements or data attributes. */
  tier: BreakpointTier;
}

function computeState(width: number): BreakpointState {
  const isMobile = width < BREAKPOINTS.tablet;
  const isTablet =
    width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop;
  const isDesktop =
    width >= BREAKPOINTS.desktop && width < BREAKPOINTS.wide;
  const isWide = width >= BREAKPOINTS.wide;

  let tier: BreakpointTier;
  if (isMobile) tier = "mobile";
  else if (isTablet) tier = "tablet";
  else if (isDesktop) tier = "desktop";
  else tier = "wide";

  return { width, isMobile, isTablet, isDesktop, isWide, tier };
}

export function useBreakpoint(): BreakpointState {
  const [width, setWidth] = useState<number>(() =>
    typeof window === "undefined" ? 0 : window.innerWidth,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Sync once on mount in case the initial state was 0 (SSR hydration).
    setWidth(window.innerWidth);

    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setWidth(window.innerWidth);
      });
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(frame);
    };
  }, []);

  return computeState(width);
}